import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ViewIconComponent, ArrowLeftIconComponent } from '@kuiper/icons';
import { BadgeComponent } from '../../../../../../ui-components/lib/badge/badge.component';
import { RequestCard } from '../../../../../common/models/request.model';
import { RequestTypeEnum } from '../../../../../common/enums/request-type';
import { AppPermissions } from '../../../../../common/constants/app-permissions';
import { PermissionsDirective } from '../../../../../../ui-components/directives/permissions/permissions.directive';
import { TenantService } from '../../../../../common/services/tenant/tenant.service';
import { EMPLOYEE_ROUTES } from '../../../../../common/constants/employee.routing.constants';
import { PreviousRouteService } from '../../../../../common/services/previous-route/previous-route.service';
import { FeatureFlagsService } from '../../../../../common/services/feature-flags/feature-flags.service';
import { FEATURE_FLAGS } from '../../../../../common/constants/feature-flags.constants';
import {
  VENDOR_MAIN_ROUTE,
  VENDOR_ROUTES,
} from '../../../../../common/constants/vendor.routing.constants';
import { RequestService } from '../../../../../common/services/request/request.service';
import { injectFloatingLayout } from '../../../../../common/helpers/system-tabs-layout/system-tabs-layout';
import { INSIGHT_CHART_FALLBACK_COLOR } from '../../../../../common/components/insight-charts/insight-chart.model';
import { RequestActionsComponent } from '../request-actions/request-actions.component';

@Component({
  selector: 'lib-request-card',
  imports: [
    BadgeComponent,
    PermissionsDirective,
    RouterLink,
    ViewIconComponent,
    ArrowLeftIconComponent,
    RequestActionsComponent,
  ],
  templateUrl: './request-card.component.html',
  styleUrl: './request-card.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class RequestCardComponent {
  readonly EMPLOYEE_ROUTES = EMPLOYEE_ROUTES;
  readonly VENDOR_ROUTES = VENDOR_ROUTES;
  readonly VENDOR_MAIN_ROUTE = VENDOR_MAIN_ROUTE;
  router = inject(Router);
  route = inject(ActivatedRoute);
  previousRouteService = inject(PreviousRouteService);
  tenantService = inject(TenantService);
  private readonly requestService = inject(RequestService);
  private readonly featureFlagsService = inject(FeatureFlagsService);

  isCardRedesignedEnabled = signal<boolean>(
    this.featureFlagsService.getFeatureFlagValue(
      FEATURE_FLAGS.WORKFLOWS.card_redesigned
    )
  );

  readonly isInsightCardsNewDesign = signal(
    this.featureFlagsService.getFeatureFlagValue(
      FEATURE_FLAGS.SYSTEM.insight_cards_new_design
    )
  );

  request = input.required<RequestCard>();

  protected readonly floating = injectFloatingLayout();
  /** The category's own colour, grey when it has none. */
  protected readonly categoryColor = computed(
    () => this.request().category.color || INSIGHT_CHART_FALLBACK_COLOR
  );

  readonly categoryBorderColor = computed(() => {
    if (!this.isInsightCardsNewDesign()) {
      return null;
    }
    const color = this.request().category.color;
    return color ? `${color}4D` : null;
  });
  appPermissions = AppPermissions;
  navigationPath = computed(() =>
    this.requestService.navigationPath(
      `${EMPLOYEE_ROUTES.REQUESTS.DETAILS}/${this.request().id}`
    )
  );

  savePreviousParams() {
    const queryParams = this.route.snapshot.queryParams;
    this.previousRouteService.setPreviousParams(queryParams);
  }

  triggerRequest(id: number) {
    const queryParams = this.route.snapshot.queryParams;
    this.previousRouteService.setPreviousParams(queryParams);
    const subPath = `${EMPLOYEE_ROUTES.REQUESTS.DETAILS}/${id}/${RequestTypeEnum.TRIGGER}`;
    const path = this.requestService.navigationPath(subPath);
    this.router.navigate([path]);
  }
}
